import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

function DeltaBadge({ value, invert = false, suffix = "" }) {
  if (value === null || value === undefined) return <span className="tm-badge bg-[#3B424B]/10 text-[#3B424B]">—</span>;
  const good = invert ? value < 0 : value > 0;
  const neutral = value === 0;
  const cls = neutral ? "bg-[#3B424B]/10 text-[#3B424B]" : good ? "bg-[#14624A]/10 text-[#14624A]" : "bg-[#D9381E]/10 text-[#D9381E]";
  const Icon = neutral ? Minus : good ? TrendingUp : TrendingDown;
  return <span className={`tm-badge ${cls}`}><Icon size={12} /> {value > 0 ? "+" : ""}{value}{suffix}</span>;
}

export default function ReportCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/report-card").then((r) => setData(r.data)).finally(() => setLoading(false)); }, []);

  return (
    <DashboardLayout>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="report-card-heading">This week's report card</h1>
      <p className="mt-1 text-sm text-[#3B424B]">How your dealership moved vs the prior 7-day window.</p>

      {loading ? <div className="mt-6 text-sm text-[#3B424B]">Loading…</div> : !data?.has_data ? (
        <div className="mt-6 tm-card p-8 bg-white" data-testid="report-card-empty">Upload inventory + sales data to see your weekly progress.</div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="tm-card p-6 bg-white" data-testid="rc-coverage">
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Active-SKU coverage</div>
            <div className="mt-2 flex items-baseline gap-2"><div className="text-3xl font-bold font-mono">{data.coverage_pct_now}%</div><DeltaBadge value={data.coverage_delta} suffix="pp" /></div>
            <div className="mt-1 text-xs text-[#3B424B]">Previously {data.coverage_pct_prev}%</div>
          </div>
          <div className="tm-card p-6 bg-white" data-testid="rc-reorder">
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Reorder gaps</div>
            <div className="mt-2 flex items-baseline gap-2"><div className="text-3xl font-bold font-mono">{data.reorder_count_now}</div><DeltaBadge value={data.reorder_gaps_closed} suffix=" closed" /></div>
            <div className="mt-1 text-xs text-[#3B424B]">Previously {data.reorder_count_prev}</div>
          </div>
          <div className="tm-card p-6 bg-white" data-testid="rc-margin">
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Average margin</div>
            <div className="mt-2 flex items-baseline gap-2"><div className="text-3xl font-bold font-mono">{data.margin_pct_now ?? "—"}%</div><DeltaBadge value={data.margin_delta_pct} suffix="pp" /></div>
            <div className="mt-1 text-xs text-[#3B424B]">Previously {data.margin_pct_prev ?? "—"}%</div>
          </div>
          <div className="tm-card p-6 bg-white sm:col-span-2 lg:col-span-3">
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Snapshot</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><div className="text-[11px] text-[#3B424B] uppercase tracking-wider">SKUs</div><div className="font-mono font-semibold mt-1">{data.total_skus}</div></div>
              <div><div className="text-[11px] text-[#3B424B] uppercase tracking-wider">Capital tied up (slow)</div><div className="font-mono font-semibold mt-1">${data.capital_tied_up_slow}</div></div>
              <div><div className="text-[11px] text-[#3B424B] uppercase tracking-wider">Reorder this week</div><div className="font-mono font-semibold mt-1">{data.reorder_count_now}</div></div>
              <div><div className="text-[11px] text-[#3B424B] uppercase tracking-wider">Report generated</div><div className="font-mono font-semibold mt-1">{new Date(data.generated_at).toLocaleDateString()}</div></div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
